import React from 'react';
import { money, signed, pct, signedPct, tone, mmdd } from './format';

const act = a => {
  if (!a || a === 'Hold') return 'df-muted';
  if (/roll|close/i.test(a)) return 'down';
  return 'up';
};

// Columns are text unless listed here; alignment of header and cells comes from
// the same place so they can't drift apart.
const TEXT = new Set(['sym', 'type', 'act']);

// render(row) returns one entry per column: a value, or [value, className].
const Section = ({ title, rows, pl, cols, render }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <article className="df-wide">
      <header><h3>{title} <span className="df-muted">{rows.length}</span></h3><span className={tone(pl)}>{signed(pl)}</span></header>
      <table className="df-table">
        <thead><tr>{cols.map(c => <th key={c} className={TEXT.has(c) ? '' : 'df-num'}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {render(r).map((cell, j) => {
                const [v, cls] = Array.isArray(cell) ? cell : [cell, ''];
                return <td key={cols[j]} className={`${TEXT.has(cols[j]) ? '' : 'df-num'} ${cls || ''}`}>{v}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </article>
  );
};

const pft = p => [pct(p * 100), p >= 0.5 ? 'up' : p < 0 ? 'down' : ''];
const dte = d => [d, d != null && d <= 5 ? 'down' : ''];
const pl = v => [signed(v), tone(v)];
const action = a => [a, act(a)];

const SINGLE = ['sym', 'price', 'strike', 'otm', 'exp', 'dte', 'qty', 'prem', 'curr', '%pft', 'p/l', 'act'];
const single = r => [
  r.underlying, money(r.price, 0), money(r.strike, 0), signedPct(r.otm_pct, 1), mmdd(r.exp_date), dte(r.dte),
  r.qty, money(r.cost, 2), money(r.current, 2), pft(r.profit_pct), pl(r.pl), action(r.action),
];

const VERTICAL = ['sym', 'type', 'strikes', 'width', 'exp', 'dte', 'qty', 'entry', 'curr', '%pft', 'p/l', 'act'];
const vertical = r => [
  r.underlying, r.type, `${money(r.short_strike, 0)}/${money(r.long_strike, 0)}`, money(r.width, 0), mmdd(r.exp_date), dte(r.dte),
  r.qty, money(r.entry, 2), money(r.current, 2), pft(r.profit_pct), pl(r.pl), action(r.action),
];

export default function Positions({ data }) {
  const p = data.positions;
  const spl = data.strategy_pl || {};
  const total = Object.values(spl).reduce((a, b) => a + b, 0);

  return (
    <div className="df-positions">
      <p className="df-line">
        <span>total options p/l</span><b className={tone(total)}>{signed(total)}</b>
        <span className="df-muted">as of {mmdd(data.taken_at)}</span>
      </p>
      <div className="df-grid">
        <Section title="covered calls" rows={p.covered_calls} pl={spl.covered_calls} cols={SINGLE} render={single} />
        <Section title="cash secured puts" rows={p.csps} pl={spl.csps} cols={SINGLE} render={single} />
        <Section title="put spreads" rows={p.put_spreads} pl={spl.put_spreads} cols={VERTICAL} render={vertical} />
        <Section title="call spreads" rows={p.call_spreads} pl={spl.call_spreads} cols={VERTICAL} render={vertical} />
        <Section title="iron condors" rows={p.iron_condors} pl={spl.iron_condors}
          cols={['sym', 'puts', 'calls', 'exp', 'dte', 'qty', 'entry', 'curr', '%pft', 'p/l', 'act']}
          render={r => [
            r.underlying, `${money(r.put_short, 0)}/${money(r.put_long, 0)}`, `${money(r.call_short, 0)}/${money(r.call_long, 0)}`,
            mmdd(r.exp_date), dte(r.dte), r.qty, money(r.entry, 2), money(r.current, 2), pft(r.profit_pct), pl(r.pl), action(r.action),
          ]} />
        <Section title="calendars" rows={p.calendars} pl={spl.calendars}
          cols={['sym', 'type', 'strike', 'short exp', 'long exp', 'dte', 'qty', 'debit', 'curr', '%pft', 'p/l', 'act']}
          render={r => [
            r.underlying, r.type, money(r.strike, 0), mmdd(r.short_exp), mmdd(r.long_exp), `${r.short_dte}/${r.long_dte}`,
            r.qty, money(r.entry, 2), money(r.current, 2), pft(r.profit_pct), pl(r.pl), action(r.action),
          ]} />
        <Section title="pmcc / diagonals" rows={p.diagonals} pl={spl.diagonals}
          cols={['sym', 'type', 'long', 'short', 'short exp', 'long exp', 'dte', 'qty', 'debit', 'curr', 'p/l', 'act']}
          render={r => [
            r.underlying, r.type, money(r.long_strike, 0), money(r.short_strike, 0), mmdd(r.short_exp), mmdd(r.long_exp),
            `${r.short_dte}/${r.long_dte}`, r.qty, money(r.entry, 2), money(r.current, 2), pl(r.pl), action(r.action),
          ]} />
        <Section title="call leaps" rows={p.call_leaps} pl={spl.call_leaps} cols={SINGLE} render={single} />
        <Section title="put leaps" rows={p.put_leaps} pl={spl.put_leaps} cols={SINGLE} render={single} />
        <Section title="other" rows={p.other} pl={spl.other} cols={SINGLE} render={single} />
        <Section title="stock" rows={p.stocks} pl={p.stocks.reduce((a, s) => a + (s.pl || 0), 0)}
          cols={['sym', 'qty', 'price', 'value', 'p/l']}
          render={r => [r.symbol, r.qty, money(r.price, 2), money(r.value), pl(r.pl)]} />
      </div>
    </div>
  );
}
